import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { App, Badge, Button, Select, Tag } from "antd";
import {
  ExclamationCircleFilled,
  HomeOutlined,
  PlusCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router";
import Table from "../../Common/Table";
import UserPreview from "./Preview";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import { RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { useUsersTableGet, UserTableRow } from "../../../hooks/api/users";
import { queryFilterParams, getSorterParams } from "../../../utils";
import {
  UserStatus,
  UserPlanDisplay,
  UserRoleDisplay,
  UserStatusDisplay,
} from "../../../enums/user";
import { UserStatusType } from "../../../store/auth";
import { dateTimeFormat } from "../../../contstant";

export enum UserStatusColor {
  "active" = "green",
  // Gold, which is antd's warning: pending is a user who has been invited and
  // has not answered, so it is something waiting on someone rather than a state
  // the account is settled in. Blue said "fine", which it is not yet.
  "pending" = "gold",
  "deactivated" = "orange",
  "banned" = "red",
  "Deleted" = "gray",
}

export function getUserStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  // colorFill, as the CSS variable antd emits: this is a plain function, so
  // there is no token hook to read it from.
  return UserStatusColor[status?.toLowerCase()] || "var(--ant-color-fill)";
}
const columns = (t: TFunction): ProColumns<UserTableRow>[] => [
  {
    title: "Name",
    dataIndex: "fullName",
    key: "fullName",
    sorter: true,
    hideInSearch: true,
  },
  {
    title: t("email"),
    dataIndex: "email",
    key: "email",
    hideInSearch: true,
    sorter: true,
  },
  {
    title: t("pages.users.role"),
    dataIndex: "role",
    key: "role",
    sorter: true,
    valueEnum: UserRoleDisplay,
    renderFormItem: (_, { defaultRender, ...config }) => (
      <Select
        {...config}
        mode="multiple"
        placeholder={t("placeholder.select")}
        showSearch={{ optionFilterProp: "label" }}
        value={
          Array.isArray(config.value) ? config.value : config.value?.split(",")
        }
      />
    ),
  },
  {
    title: t("pages.users.plan"),
    dataIndex: "plan",
    key: "plan",
    sorter: true,
    valueType: "select",
    valueEnum: UserPlanDisplay,
  },
  {
    title: t("common.table.createdAt"),
    dataIndex: "createdAt",
    key: "createdAt",
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: true,
    valueEnum: UserStatusDisplay,
    render: (_, record) => (
      <Badge
        color={getUserStatusColor(record.status)}
        text={<span className="capitalize-first-letter">{record.status}</span>}
      />
    ),
  },
];

function ToolBar() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return [
    <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        navigate("create");
      }}
    >
      {t("common.table.add")}
    </Button>,
  ];
}

export default function Users(): React.ReactNode | null {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  // The row being previewed. A preview is something read beside the list, so it
  // opens a drawer and the table stays where it was.
  const [previewRow, setPreviewRow] = useState<UserTableRow | null>(null);
  const { modal, message } = App.useApp();
  /**
   * Users deleted in this session, by id.
   *
   * TODO(design): nothing is deleted. Connecting `useUserDelete` changes what
   * the screen sends, which is a developer's; until then the row is only
   * hidden and comes back on the next load. See DEVELOPER_TASKS.md.
   */
  const [deleted, setDeleted] = useState<string[]>([]);

  // TODO(design): nothing is sent. The invitation endpoint is a developer's to
  // connect — see DEVELOPER_TASKS.md.
  const resendInvitation = (row: UserTableRow) =>
    message.success(
      t("pages.users.invitationResent", {
        name: row.fullName || row.email,
      }),
    );

  const confirmDelete = (row: UserTableRow) =>
    modal.confirm({
      icon: <ExclamationCircleFilled />,
      title: t("pages.users.deleteUserTitle"),
      content: (
        <span>
          {t("pages.users.deleteUser")} <b>{row.fullName || row.email}</b>?
        </span>
      ),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: () => {
        setDeleted((ids) => [...ids, row.id]);
        message.success(
          t("pages.users.deleted", { name: row.fullName || row.email }),
        );
      },
    });
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const usersGet = useUsersTableGet();

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: t("routes.users") },
    ]);
  }, [i18n.language, t]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });
    console.log({
      ...newParams,
      role: undefined,
      roles: newParams.role ? newParams?.role?.split(",") : undefined,
    });
    return usersGet
      .fetch({
        ...newParams,
        role: undefined,
        roles: newParams.role ? newParams?.role?.split(",") : undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            // Display only: what is asked for has not changed, and a user
            // hidden here is one this session deleted. It goes with the local
            // list above, once there is an endpoint behind it.
            data: (data || [])
              .filter((item) => !deleted.includes(item.id))
              .map((item) => ({
                ...item,
                key: item.id,
              })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <>
      <UserPreview
        row={previewRow}
        onClose={() => setPreviewRow(null)}
        onEdit={(row) => {
          setPreviewRow(null);
          navigate(`${row.id}/edit`);
        }}
        // Deleting is offered where the user can be seen whole, not as an icon
        // among the row's other icons — see Common/DeleteButton.
        onDelete={(row) => {
          setPreviewRow(null);
          confirmDelete(row);
        }}
        onResend={resendInvitation}
      />

      <Table<UserTableRow>
        key={`${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className={clsx("transparent")}
        headerTitle={t("pages.users.tableName")}
        toolBarRender={ToolBar}
        request={tableRequest}
        columns={columns(t)}
        actions={[
          [
            "view",
            ({ record }) => ({
              title: t("common.preview"),
              onClick: (e: MouseEvent) => {
                e.preventDefault();
                setPreviewRow(record);
              },
            }),
          ],
          [
            "reload",
            ({ record }) => ({
              title: t("pages.users.resendInvitation"),
              onClick: (e: MouseEvent) => {
                e.preventDefault();
                resendInvitation(record);
              },
            }),
            // Only a user who was invited and has not answered: there is
            // nothing to resend to anyone else.
            ({ record }) => record.status === UserStatus.PENDING,
          ],
          [
            "edit",
            ({ record }) => ({
              title: t("common.edit"),
              onClick: (e: MouseEvent) => {
                e.preventDefault();
                navigate(`${record.id}/edit`);
              },
            }),
          ],
        ]}
        search={{
          style: {
            // display: 'none',
          },
        }}
        options={{
          search: {
            name: "fullName",
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-users",
          persistenceType: "localStorage",
        }}
      />
    </>
  );
}
